import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { useToast } from '@/hooks/use-toast';
import { apiPost } from '@/lib/api/client';
import { usePermission } from '@/lib/contexts/PermissionContext';
import { OrganizationSignUp } from '@/types/auth';

interface UseSubmitOrganizationReturn {
  handleSubmitOrganization: (organization: OrganizationSignUp) => Promise<void>;
  isLoading: boolean;
  error: string;
  validationErrors: Record<string, string>;
}

export const useSubmitOrganization = (): UseSubmitOrganizationReturn => {
  const router = useRouter();
  const { toast } = useToast();
  const { refreshRole } = usePermission();
  const [error, setError] = useState<string>('');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSubmitOrganization = async (
    organizationFormState: OrganizationSignUp
  ): Promise<void> => {
    setIsLoading(true);
    setError('');
    setValidationErrors({});

    try {
      // 共通クライアント経由(credentials: 'include')でないと
      // サインアップ成功時の認証Cookieがブラウザに保存されない
      const result = await apiPost<{ Message: string }>(
        '/auth/setup-new-organization',
        {
          OrganizationName: organizationFormState.organizationName,
          FullName: organizationFormState.fullName,
          Email: organizationFormState.email,
          Password: organizationFormState.password,
          PasswordConfirm: organizationFormState.confirmPassword,
          AcceptedTerms: organizationFormState.acceptedTerms,
        },
        { skipAuthHeaders: true, skipAuthRedirect: true }
      );

      // バリデーションエラー: トースター + 各フィールド下にメッセージ表示
      if (result.validationErrors) {
        setValidationErrors(result.validationErrors);
        toast({
          description: 'エラーが発生しました。入力内容を確認してください。',
          variant: 'destructive',
        });
        return;
      }

      // フィールドに紐づかないエラー: メッセージをトースターで表示
      if (result.error) {
        setError(result.error);
        toast({ description: result.error, variant: 'destructive' });
        return;
      }

      // サインアップ成功でCookieが発行されたので、ロールをサーバから再取得する
      // (SPA遷移ではPermissionProviderが再マウントされないため明示的に更新)
      await refreshRole();

      router.push('/onboarding');
    } catch (err) {
      const message = err instanceof Error ? err.message : '組織登録に失敗しました（不明なエラー）';
      setError(message);
      toast({ description: message, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  return { handleSubmitOrganization, isLoading, error, validationErrors };
};
